import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine, FadeUp } from "./Reveal";
import { LogoLockup } from "./Logo";
import { IMAGES, scrollToSection } from "../constants/site";

const STATS = [
  ["1,200+", "Acres Under Management"],
  ["38", "Estate Clients"],
  ["21", "Harvests Completed"],
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[720px] overflow-hidden bg-[#12271B]">
      <motion.video
        poster={IMAGES.hero}
        autoPlay
        muted
        loop
        playsInline
        aria-label="Aerial footage over a Napa Valley vineyard at first light"
        style={{ y, scale }}
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="/assets/hero-video.mp4" type="video/mp4" />
        <source src="/assets/hero-video.webm" type="video/webm" />
      </motion.video>
      <div className="absolute inset-0 bg-gradient-to-b from-[#12271B]/55 via-[#12271B]/25 to-[#12271B]/80" />

      <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col items-center justify-center text-center pb-24">
        <h1 data-testid="hero-title" className="max-w-full">
          <span className="sr-only">Vinedresser Vineyard Management</span>
          <motion.div
            initial={{ clipPath: "inset(0 100% 0 0)", y: 24, opacity: 0.4 }}
            animate={{ clipPath: "inset(0 0% 0 0)", y: 0, opacity: 1 }}
            transition={{ duration: 1.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <LogoLockup light size="hero" align="center" className="select-none pointer-events-none" />
          </motion.div>
        </h1>

        <MaskLine delay={0.9} className="mt-10 md:mt-12 w-full text-center">
          <p data-testid="hero-subline" className="font-display italic font-light text-[#F9F6F0]/90 text-2xl md:text-[2rem] leading-snug">
            Premium farming for vineyards and winegrowing estates of Napa and Sonoma.
          </p>
        </MaskLine>
        <FadeUp delay={1.2} className="mt-10">
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollToSection("contact")}
            className="group inline-flex items-center gap-3 bg-[#F9F6F0] text-[#12271B] text-[11px] tracking-[0.22em] uppercase font-semibold px-8 py-4 transition-colors duration-300 hover:bg-[#B89B67] active:scale-95"
          >
            Start a conversation
            <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </FadeUp>
      </div>

      <FadeUp delay={1.05} className="relative z-10">
        <div data-testid="hero-stats" className="border-t border-[#F9F6F0]/15">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex flex-wrap gap-x-12 gap-y-3">
            {STATS.map(([num, label]) => (
              <div key={label} className="flex items-baseline gap-3">
                <span className="font-display text-2xl text-[#F9F6F0]">{num}</span>
                <span className="text-[10px] tracking-[0.24em] uppercase text-[#F9F6F0]/60">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </FadeUp>
    </section>
  );
};
